package com.qrkontaq.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.qrkontaq.ui.tenant.GuestApi
import com.qrkontaq.ui.tenant.GuestSession
import com.qrkontaq.ui.tenant.InlineIcon
import com.qrkontaq.ui.tenant.MenuIcons
import com.qrkontaq.ui.theme.Brand
import com.qrkontaq.utils.OAuthHelper
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.border
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexGrow
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.height
import com.varabyte.kobweb.compose.ui.modifiers.id
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.maxWidth
import com.varabyte.kobweb.compose.ui.modifiers.minHeight
import com.varabyte.kobweb.compose.ui.modifiers.onClick
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.size
import com.varabyte.kobweb.compose.ui.modifiers.textAlign
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vh
import org.jetbrains.compose.web.dom.Div
import org.w3c.dom.url.URL
import org.w3c.dom.url.URLSearchParams
import kotlin.js.json

private const val AUTH_GOOGLE_BUTTON_ID = "qk-auth-google"

/**
 * The one fixed origin where guest OAuth actually runs (auth.qrkontaq.com; /auth
 * on localhost in dev) — Google/Apple require pre-registered origins, so venue
 * subdomains can't host the flows themselves.
 *
 * Opened by a venue site either as a POPUP (postMessage the token back, close)
 * or via a full REDIRECT for mobile/in-app browsers that block popups (the
 * venue page then picks the session up from the httpOnly cookie).
 */
@Page("/auth")
@Composable
fun AuthGatewayPage() {
    val params = remember { URLSearchParams(window.location.search) }
    val provider = remember { params.get("provider")?.lowercase() }
    val returnOrigin = remember { params.get("return_origin")?.takeIf(::isTrustedOrigin) }
    val returnUrl = remember {
        params.get("return_url")?.takeIf { url ->
            runCatching { isTrustedOrigin(URL(url).origin) }.getOrDefault(false)
        }
    }
    val redirectMode = remember { params.get("mode") == "redirect" }
    val lang = remember { params.get("lang")?.takeIf { it == "en" } ?: "uk" }

    var busy by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var done by remember { mutableStateOf(false) }
    val scope = rememberCoroutineScope()

    fun deliverSession() {
        done = true
        val payload = json(
            "type" to "qk-auth-success",
            "accessToken" to GuestSession.token,
            "email" to GuestSession.email,
            "provider" to GuestSession.oauthProvider,
        )
        val opener = window.asDynamic().opener
        when {
            redirectMode && returnUrl != null -> window.location.href = returnUrl
            opener != null && returnOrigin != null -> {
                opener.postMessage(payload, returnOrigin)
                window.close()
            }
            returnUrl != null -> window.location.href = returnUrl
        }
    }

    fun failed() {
        busy = false
        error =
            if (lang == "en") "Sign-in failed. Please try again."
            else "Не вдалося увійти. Спробуйте ще раз."
    }

    fun onGoogleCredential(idToken: String, email: String?, name: String?, picture: String?) {
        busy = true
        scope.launch {
            if (GuestApi.loginWithGoogle(idToken, email, name, picture)) deliverSession() else failed()
        }
    }

    LaunchedEffect(Unit) {
        // Utility screen: keep it out of the index (it otherwise dupes the
        // homepage title and can surface in SERPs via stray external links).
        com.qrkontaq.utils.SeoHelper.setRobots("noindex, follow")
        OAuthHelper.signInWithGoogleButton(
            buttonElementId = AUTH_GOOGLE_BUTTON_ID,
            onSuccess = { user -> onGoogleCredential(user.idToken, user.email, user.name, user.picture) },
            onError = { failed() },
        )
        if (provider != "apple") {
            // One Tap shows without a user gesture — for a guest with a live
            // Google session this makes the whole flow a single tap.
            OAuthHelper.promptGoogleOneTap()
        }
    }

    // Full-viewport warm backdrop with a soft emerald glow behind the card.
    Column(
        Modifier
            .fillMaxWidth()
            .minHeight(100.vh)
            .padding(20.px)
            .styleModifier {
                property(
                    "background",
                    "radial-gradient(560px 320px at 50% 12%, rgba(15,157,110,0.10), transparent 70%), " +
                        "linear-gradient(165deg, #FAF8F5 0%, #F1F6F2 100%)",
                )
                property("font-family", "'Inter', system-ui, sans-serif")
            },
        horizontalAlignment = Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        Column(
            Modifier
                .fillMaxWidth()
                .maxWidth(392.px)
                .backgroundColor(Brand.Surface)
                .borderRadius(22.px)
                .padding(topBottom = 30.px, leftRight = 26.px)
                .styleModifier {
                    property("box-shadow", "0 24px 60px -24px rgba(31,36,33,0.22), 0 2px 8px rgba(31,36,33,0.05)")
                },
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            // Wordmark: emerald QR tile + name.
            Row(
                Modifier.margin(bottom = 20.px).gap(10.px),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Box(
                    Modifier
                        .size(38.px)
                        .borderRadius(11.px)
                        .styleModifier {
                            property("background", "linear-gradient(150deg, #0F9D6E, #0B7A55)")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                            property("box-shadow", "0 6px 14px -6px rgba(15,157,110,0.55)")
                        },
                ) {
                    SpanText(
                        "QR",
                        Modifier.fontSize(14.px).fontWeight(FontWeight.Bold).color(Brand.Surface)
                            .styleModifier { property("letter-spacing", "0.02em") },
                    )
                }
                SpanText(
                    "QRkontaq",
                    Modifier.fontSize(21.px).fontWeight(FontWeight.Bold).color(Brand.Charcoal)
                        .styleModifier { property("letter-spacing", "-0.02em") },
                )
            }

            SpanText(
                when {
                    done -> if (lang == "en") "You're in!" else "Вхід виконано!"
                    busy -> if (lang == "en") "Signing you in…" else "Виконуємо вхід…"
                    else -> if (lang == "en") "Sign in" else "Вхід"
                },
                Modifier.fontSize(24.px).fontWeight(FontWeight.Bold).color(Brand.Charcoal)
                    .styleModifier { property("letter-spacing", "-0.02em") },
            )
            SpanText(
                when {
                    done -> if (lang == "en") "This window can be closed" else "Це вікно можна закрити"
                    busy -> if (lang == "en") "Just a moment…" else "Одну мить…"
                    else ->
                        if (lang == "en") "Collect cashback at your favourite places"
                        else "Накопичуйте кешбек у ваших улюблених закладах"
                },
                Modifier
                    .fontSize(13.5.px)
                    .color(Brand.TextSecondary)
                    .margin(top = 6.px, bottom = 22.px)
                    .textAlign(TextAlign.Center)
                    .styleModifier { property("line-height", "1.55") },
            )

            if (done) {
                Box(
                    Modifier.size(52.px).borderRadius(999.px)
                        .styleModifier {
                            property("background", "rgba(15,157,110,0.12)")
                            property("display", "flex")
                            property("align-items", "center")
                            property("justify-content", "center")
                        },
                ) {
                    SpanText("✓", Modifier.fontSize(26.px).fontWeight(FontWeight.Bold).color(Brand.Emerald))
                }
            } else if (!busy) {
                // Google: styled row with the real (invisible) GIS button stretched
                // on top — the click lands on Google's iframe, our row gives the look.
                Box(
                    Modifier.fillMaxWidth().styleModifier { property("position", "relative") },
                ) {
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .height(46.px)
                            .backgroundColor(Brand.Surface)
                            .border(1.px, LineStyle.Solid, Brand.Divider)
                            .borderRadius(12.px)
                            .attrsModifier { classes("qk-press") }
                            .styleModifier { property("box-shadow", "0 1px 3px rgba(31,36,33,0.06)") },
                        horizontalArrangement = Arrangement.Center,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        InlineIcon(MenuIcons.GoogleLogo, 18, "#1F2421")
                        SpanText(
                            if (lang == "en") "Continue with Google" else "Продовжити з Google",
                            Modifier.fontSize(14.5.px).fontWeight(FontWeight.SemiBold)
                                .color(Brand.Charcoal).margin(left = 9.px),
                        )
                    }
                    Div(
                        attrs = {
                            id(AUTH_GOOGLE_BUTTON_ID)
                            style {
                                property("position", "absolute")
                                property("inset", "0")
                                property("display", "flex")
                                property("justify-content", "center")
                                property("overflow", "hidden")
                                property("opacity", "0.001")
                            }
                        },
                    )
                }

                if (provider != "google") {
                    // divider
                    Row(
                        Modifier.fillMaxWidth().margin(topBottom = 14.px).gap(12.px),
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        Box(Modifier.flexGrow(1).height(1.px).backgroundColor(Brand.Divider)) {}
                        SpanText(
                            if (lang == "en") "or" else "або",
                            Modifier.fontSize(12.px).color(Brand.TextSecondary),
                        )
                        Box(Modifier.flexGrow(1).height(1.px).backgroundColor(Brand.Divider)) {}
                    }

                    // Apple
                    Row(
                        Modifier
                            .fillMaxWidth()
                            .height(46.px)
                            .backgroundColor(Brand.Charcoal)
                            .borderRadius(12.px)
                            .cursor(Cursor.Pointer)
                            .attrsModifier { classes("qk-press"); attr("role", "button") }
                            .onClick {
                                if (!busy) {
                                    busy = true
                                    error = null
                                    OAuthHelper.signInWithApple(
                                        onSuccess = { idToken, email, name ->
                                            scope.launch {
                                                if (GuestApi.loginWithApple(idToken, email, name)) {
                                                    deliverSession()
                                                } else {
                                                    failed()
                                                }
                                            }
                                        },
                                        onError = { failed() },
                                        // Cancelled: just clear the spinner, no error.
                                        onCancel = { busy = false },
                                    )
                                }
                            },
                        horizontalArrangement = Arrangement.Center,
                        verticalAlignment = Alignment.CenterVertically,
                    ) {
                        InlineIcon(MenuIcons.AppleLogo, 18, "#FFFFFF")
                        SpanText(
                            if (lang == "en") "Continue with Apple" else "Продовжити з Apple",
                            Modifier.fontSize(14.5.px).fontWeight(FontWeight.SemiBold)
                                .color(Brand.Surface).margin(left = 9.px),
                        )
                    }
                }
            }

            error?.let {
                SpanText(
                    it,
                    Modifier.fontSize(12.5.px).color(Brand.CoralText).margin(top = 14.px)
                        .textAlign(TextAlign.Center),
                )
            }
        }

        SpanText(
            if (lang == "en") {
                "One account for every place on QRkontaq"
            } else {
                "Один акаунт для всіх закладів на QRkontaq"
            },
            Modifier.fontSize(12.px).color(Brand.TextSecondary).margin(top = 18.px)
                .textAlign(TextAlign.Center),
        )
    }
}

/** Only qrkontaq.com and its subdomains (or localhost in dev) may receive tokens. */
private fun isTrustedOrigin(origin: String): Boolean {
    if (origin.startsWith("http://localhost:") || origin == "http://localhost" ||
        origin.startsWith("http://127.0.0.1:") || origin == "http://127.0.0.1"
    ) {
        return true
    }
    return Regex("^https://([a-z0-9-]+\\.)?qrkontaq\\.com$").matches(origin)
}
